import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import { RagAgent } from "../api/ragApi";
import { fetchAgentsThunk, getRagAgents, getRagAgentsStatus } from "../state/ragSlice";
import { SearchInput } from "src/common/components/SearchInput";
import WixarBadge from "./WixarBadge";

const AGENT_ICONS: Record<string, string> = {
  default: "\uD83E\uDD16",
  book: "\uD83D\uDCDA",
  support: "\uD83C\uDFA7",
  brain: "\uD83E\uDDE0",
  search: "\uD83D\uDD0D",
  chat: "\uD83D\uDCAC",
  star: "\u2B50",
  tool: "\uD83D\uDD27",
  idea: "\uD83D\uDCA1",
  language: "\uD83C\uDF0D",
  education: "\uD83C\uDF93",
  expert: "\uD83D\uDC54",
};

interface RagAgentMultiSelectProps {
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  /**
   * When true, the agent list grows to fill the parent's available height
   * (and scrolls internally) instead of being capped at a fixed 200px.
   * The parent must be a flex column with a definite height for this to work.
   * Defaults to false so existing call sites keep the fixed-height behaviour.
   */
  fillHeight?: boolean;
}

const RagAgentMultiSelect = ({
  value,
  onChange,
  disabled = false,
  title,
  description,
  fillHeight = false,
}: RagAgentMultiSelectProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const agents = useSelector(getRagAgents);
  const status = useSelector(getRagAgentsStatus);
  const loading = status === "idle" || status === "loading";
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchAgentsThunk());
  }, [dispatch]);

  // Clean up selected IDs that reference deleted agents
  useEffect(() => {
    if (agents.length === 0 || value.length === 0) return;
    const existingIds = new Set(agents.map((a) => a.id));
    const validIds = value.filter((id) => existingIds.has(id));
    if (validIds.length !== value.length) {
      onChange(validIds);
    }
  }, [agents, value]);

  const toggleAgent = (agentId: number) => {
    if (disabled) return;
    const newValue = value.includes(agentId)
      ? value.filter((id) => id !== agentId)
      : [...value, agentId];
    onChange(newValue);
  };

  // Split into own agents and shared, filter by search. Order is STABLE
  // (alphabetical): it used to float the selected agents to the top, but
  // re-sorting on every click made rows jump under the cursor (Sébastien
  // 2026-07-16). Selection is conveyed by the checkmark + the count below.
  const { ownAgents, sharedAgents } = useMemo(() => {
    let filtered = agents;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = agents.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.description && a.description.toLowerCase().includes(q)),
      );
    }
    const sortFn = (a: RagAgent, b: RagAgent) => a.name.localeCompare(b.name);
    return {
      ownAgents: filtered.filter((a) => !a.is_shared).sort(sortFn),
      sharedAgents: filtered.filter((a) => a.is_shared).sort(sortFn),
    };
  }, [agents, search]);

  const renderAgentButton = (agent: RagAgent) => {
    const isSelected = value.includes(agent.id);
    return (
      <button
        key={agent.id}
        type="button"
        onClick={() => toggleAgent(agent.id)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all text-sm flex-shrink-0 w-full ${
          isSelected
            ? "border-green-400 bg-green-50 shadow-sm"
            : "border-gray-200 bg-white hover:border-gray-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className="text-lg flex-shrink-0">
          {AGENT_ICONS[agent.icon] || AGENT_ICONS.default}
        </span>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 truncate flex items-center gap-1">
            {agent.name}
            {agent.is_shared && <WixarBadge size="sm" />}
          </span>
          {(agent.description || agent.document_count != null) && (
            <span className="text-xs text-gray-400 truncate block">
              {agent.description ||
                t("pages.agents.documentCount", { count: agent.document_count ?? 0 })}
            </span>
          )}
        </div>
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
            isSelected ? "border-green-500 bg-green-500" : "border-gray-300"
          }`}
        >
          {isSelected && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {title || t("pages.agents.ragAgentSelect.title")}
        </h3>
        <div className="animate-pulse flex flex-col gap-2">
          <div className="h-10 bg-gray-200 rounded-lg" />
          <div className="h-10 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {title || t("pages.agents.ragAgentSelect.title")}
        </h3>
        <p className="text-sm text-gray-400">
          {t("pages.agents.ragAgentSelect.noAgentsAvailable")}
        </p>
      </div>
    );
  }

  const noResults = ownAgents.length === 0 && sharedAgents.length === 0 && search.trim();
  const totalVisible = ownAgents.length + sharedAgents.length;
  const needsScroll = totalVisible > 3;

  return (
    <div className={`mt-4 min-w-0 ${fillHeight ? "flex flex-col flex-1 min-h-0" : ""}`}>
      <h3 className="text-sm font-medium text-gray-700 mb-1">
        {title || t("pages.agents.ragAgentSelect.title")}
      </h3>
      <p className="text-xs text-gray-400 mb-3">
        {description || t("pages.agents.ragAgentSelect.description")}
      </p>

      {agents.length > 5 && (
        <div className="mb-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("pages.agents.ragAgentSelect.searchPlaceholder")}
          />
        </div>
      )}

      <div
        className={`flex flex-col gap-1.5 ${
          fillHeight
            ? "flex-1 min-h-0 overflow-y-auto"
            : needsScroll
            ? "h-[200px] overflow-y-auto"
            : ""
        }`}
      >
        {/* Shared agents from Wixar — shown first */}
        {sharedAgents.length > 0 && (
          <>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1 flex-shrink-0">
              {t("pages.agents.ragAgentSelect.sharedAgents")}
            </p>
            {sharedAgents.map(renderAgentButton)}
          </>
        )}

        {/* Own agents */}
        {ownAgents.length > 0 && (
          <>
            {sharedAgents.length > 0 && (
              <div className="border-t border-gray-200 my-1 flex-shrink-0" />
            )}
            {sharedAgents.length > 0 && (
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1 flex-shrink-0">
                {t("pages.agents.ragAgentSelect.yourAgents")}
              </p>
            )}
            {ownAgents.map(renderAgentButton)}
          </>
        )}

        {noResults && (
          <p className="text-sm text-gray-400 text-center py-3">{t("general.noResults")}</p>
        )}
      </div>

      {value.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          {t("pages.agents.ragAgentSelect.selectedCount", { count: value.length })}
        </p>
      )}
    </div>
  );
};

export default RagAgentMultiSelect;
